import { btoa } from "./btoa";
import { useProvideInject } from "./provideInjectGenerator";
import {
  AnalyticEvents,
  AnalyticsContext,
  AnalyticsBet,
  AnalyticsRequest
} from "@fresh8/types/src/analytics";
import { BetData } from "@fresh8/types/src/betData";
import { injectInsightTestingContext } from "@fresh8/ads/src/lib/provideInjectors";
import { computed, Ref, ComputedRef } from "vue";
import { useOfferCardDisplayState } from "../hooks/useDisplayOffer";
import {
  AdTag,
  AdTagCampaign,
  AdTagExperiment,
  AdTagExperimentVariant
} from "@fresh8/types/src/index";
import { injectPreroll } from "../hooks/usePreroll";
import { useInsightCardDisplayState } from "../hooks/useInsightData";
import { useParalyCardDisplayState } from "../hooks/useParlayData";

import { v4 as uuidv4 } from "uuid";
import { Preview } from "@fresh8/types/src/preview";

/**
 * Defines a new uuid for each time we load the page.
 * This viewID should be the same while it's valid.
 */
const viewIDv4 = uuidv4();

export function createAnalyticsContext(
  betData: Ref<BetData | undefined>,
  adTag: Ref<AdTag | undefined>,
  statsURL: string,
  preview?: Ref<Preview>,
  experiment?: Ref<AdTagExperiment | undefined>,
  experimentVariant?: Ref<AdTagExperimentVariant | undefined>,
  selectedCampaign?: Ref<AdTagCampaign | undefined>
): ComputedRef<AnalyticsContext | undefined> {
  const {
    offerDisplayedAsFallback,
    defaultOfferDisplayedAsFirstCard
  } = useOfferCardDisplayState();

  const { marketInsightsCardIncluded } = useInsightCardDisplayState();
  const { parlayIncluded } = useParalyCardDisplayState();

  const analyticsContext = computed(() => {
    const analytics = betData.value?.analytics;

    const pixels = betData.value?.pixels || {
      clickThrough: "",
      impression: "",
      background: ""
    };

    const urlParams = new URLSearchParams(window.location.search);
    const customField1 = urlParams.get("customField1") || undefined;
    const customField2 = urlParams.get("customField2") || undefined;
    const customField3 = urlParams.get("customField3") || undefined;
    const customField4 = urlParams.get("customField4") || undefined;
    const customField5 = urlParams.get("customField5") || undefined;
    const trackingID = urlParams.get("trackingID") || undefined;
    const analyticsDisabled = urlParams.get("analyticsDisabled") || undefined;

    let analyticsEnabled: boolean;

    if (analyticsDisabled) {
      analyticsEnabled = analyticsDisabled !== "true";
    } else {
      analyticsEnabled = process.env.NODE_ENV === "production";
    }

    const pageUrl = urlParams.get("ref") || window.location.href;
    const allowAnalytics = !preview?.value?.isPreview && analyticsEnabled;
    const campaign = selectedCampaign?.value;

    return {
      emit: allowAnalytics,
      catalogueID: analytics?.catalogueId || "",
      viewID: viewIDv4,
      pixels,
      operator: adTag.value?.operator.key || "",
      statsURL: statsURL,
      pageUrl,
      // window.adtagId is only set in prod and staging
      // The anaytlics object may not load so we want to fallback to use the window
      adtagID: window.adtagId || adTag.value?._id || "",
      campaignID: campaign?._id || "",
      brandID: adTag?.value?.brand._id || "",
      externalID: trackingID,
      campaignVariantIndex: "0",
      customField: {
        customField1,
        customField2,
        customField3,
        customField4,
        customField5
      },
      offerDisplayedAsFallback: offerDisplayedAsFallback.value,
      defaultOfferDisplayedAsFirstCard: defaultOfferDisplayedAsFirstCard.value,
      marketInsightsCardIncluded: marketInsightsCardIncluded.value,
      parlayIncluded: parlayIncluded.value,
      experimentID: experiment?.value?._id,
      experimentVariant: experimentVariant?.value?.id,
      recommenderBehaviour: analytics?.recommenderBehaviour || ""
    };
  });

  return analyticsContext;
}

const AnalyticsSymbol = Symbol("analyticsProvider");
const { injector, provider } = useProvideInject<AnalyticsContext | undefined>(
  AnalyticsSymbol,
  {} as AnalyticsContext
);

export const useAnalyticsContext = injector;
export const provideAnalyticsContext = provider;

/**
 * Base64 encodes an object with poly filling for old browsers that dont
 * support window.btoa
 * @param  {Object} data is the object to encode
 * @return {String} the base64 encode object
 */
function encodeData(data: AnalyticsRequest): string {
  let encodedData = btoa(JSON.stringify(data));
  // + and / do not travel well in URL string
  encodedData = encodedData.replace(/\//g, "_").replace(/\+/g, "-");
  return encodedData;
}
/**
 * makeRequest base64 encodes the data, sends a request using fetch and
 * returns a promise.
 * @param  {String}   URL        is the end point to make the request
 * @param  {Object}   dataObject is the data payload
 * @return {AxiosPromise}
 */
async function emitRequest(URL: string, dataObject: AnalyticsRequest) {
  const searchParams = new URLSearchParams(window.location.search);

  const encodedData = encodeData(dataObject);

  const headers = {};

  if (searchParams.get("testAnalytics")) {
    headers["F8-T"] = true;
  }

  await fetch(URL + "/d/" + encodedData, {
    method: "GET",
    mode: "cors", // no-cors, *cors, same-origin
    cache: "default",
    credentials: "include",
    headers
  });
}

function promiseImageLoad(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const img = document.createElement("img");
    img.classList.add("f8P");
    img.src = src;
    img.height = 0;
    img.width = 0;
    img.style.display = "none";

    img.addEventListener("error", e => reject(e));
    img.addEventListener("load", () => resolve());
    document.body.appendChild(img);
  });
}

function promiseIFrameLoad(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const iframe = document.createElement("iframe");
    iframe.classList.add("f8P");
    iframe.src = src;
    iframe.style.height = "0";
    iframe.style.width = "0";
    iframe.style.display = "none";

    iframe.addEventListener("error", e => reject(e));
    iframe.addEventListener("load", () => resolve());
    document.body.appendChild(iframe);
  });
}

/**
 * Fires a pixel using ajax/appending images to the DOM based on the event type
 * @param  {Object} pixels    is the pixels object containing the thrird party pixels
 * @param  {String} eventName is to fire the pixel for
 * @return {AxiosPromise}
 */
async function firePixel(
  pixelUrl: string,
  eventName: string,
  pixelType = "image"
) {
  if (eventName && eventName.indexOf(AnalyticEvents.CLICKED_THROUGH) > -1) {
    await await fetch(pixelUrl, {
      method: "GET",
      credentials: "include"
    });
  } else if (eventName && eventName.indexOf(AnalyticEvents.VIEWED) > -1) {
    pixelType === "image"
      ? await promiseImageLoad(pixelUrl)
      : await promiseIFrameLoad(pixelUrl);
  }
}

/**
 * @param {*} eventName
 * @param {*} eventDescription
 * @param {EventData} eventData
 * @param {String} eventData.viewId - View ID
 * @param {Boolean} eventData.shouldEmit - View ID
 * @param {Pixels} eventData.pixels - View ID
 * @param {String} eventData.statsURL - View ID
 * @param {String} eventData.product - View ID
 * @param {String} eventData.productType - View ID
 */
export function useHandleAnalytics(): (
  statsEventName: AnalyticEvents,
  statsEventDescription: string,
  bets?: AnalyticsBet[],
  pixelUrl?: string | undefined,
  pixelType?: "image" | "iframe"
) => Promise<AnalyticsRequest | undefined> {
  const prerollContext = injectPreroll();
  const insightContext = injectInsightTestingContext();

  const analyticsContext = useAnalyticsContext();

  return async function $handleAnalytics(
    statsEventName: AnalyticEvents,
    statsEventDescription: string,
    bets: AnalyticsBet[] = [],
    pixelUrl?: string,
    pixelType?: "image" | "iframe"
  ) {
    if (analyticsContext.value) {
      const {
        // while we have two ad-servers running
        viewID = viewIDv4,
        emit,
        statsURL,
        catalogueID,
        pageUrl,
        operator = "",
        customField = {},
        adtagID,
        campaignID,
        brandID,
        externalID,
        campaignVariantIndex,
        offerDisplayedAsFallback,
        defaultOfferDisplayedAsFirstCard,
        marketInsightsCardIncluded,
        parlayIncluded,
        experimentID,
        experimentVariant,
        recommenderBehaviour
      } = analyticsContext.value;
      const shouldEmit = emit;
      const version = "1";

      const eventData: AnalyticsRequest = {
        bets,
        viewID,
        version,
        operator,
        statsEventName,
        statsEventDescription,
        adScreenHeight: window.screen.availHeight,
        adScreenWidth: window.screen.availWidth,
        catalogueID,
        pageUrl: pageUrl.length <= 1500 ? pageUrl : pageUrl.substring(0, 1500),
        adtagID,
        campaignID,
        brandID,
        externalID,
        offerDisplayedAsFallback,
        defaultOfferDisplayedAsFirstCard,
        prerollDisplayed: !!prerollContext?.usePreroll.value,
        prerollName: prerollContext?.prerollName.value,
        marketInsightsCardIncluded:
          insightContext.value.insightRandomTesting &&
          marketInsightsCardIncluded,
        parlayIncluded,
        experimentID,
        experimentVariant,
        recommenderBehaviour
      };

      if (campaignVariantIndex !== undefined) {
        // Force campaignVariantIndex to be a string
        eventData.campaignVariantIndex = `${campaignVariantIndex}`;
      }

      // Add the custom labels to the payload
      for (const customFieldKey of Object.keys(customField)) {
        if (customField[customFieldKey]) {
          eventData[customFieldKey] = customField[customFieldKey];
        }
      }

      // If should emit is false then just return a resolved promise with the
      // constructed event data
      if (!shouldEmit) {
        // eslint-disable-next-line no-console
        console.info(
          "Analytics request data:\n" + JSON.stringify(eventData, null, 2)
        );
        return eventData;
      }

      const promises: Array<Promise<void>> = [emitRequest(statsURL, eventData)];

      if (pixelUrl) {
        // Fire clickthroughUrl in the background or create an impression pixel
        promises.push(firePixel(pixelUrl, statsEventName, pixelType));
      }

      try {
        await Promise.all(promises);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }

      return eventData;
    }
  };
}
